// src/components/MaBilletterie.jsx
// - Grille responsive (4/3 colonnes desktop, 1 colonne mobile),
//   style identique à MesEvents.jsx : image en haut, infos en bas
// - Pagination identique à MesEvents.jsx
// - Bloc statistiques/courbe MASQUÉ s'il n'y a aucune vente

import React, { useCallback, useEffect, useState } from "react";
import {
    getEvenementsBilletterie,
    getStatistiquesBilletterie,
    getReservationsCreateur,
} from "../services/BilletterieService";
import CompteStripe from "./CompteStripe";
import ModaleAnnulation from "./ModaleAnnulation";
import ModaleDemande from "./ModaleDemande";
import {
    annulerReservationOrganisateur,
    annulerEvenementOrganisateur,
    MOTIFS_RESERVATION,
    MOTIFS_EVENEMENT,
} from "../services/AnnulationService";
import { Trophy, Ticket, TrendingUp, Calendar, ImageIcon, Users, CheckCircle2, Crown, Clock, Gauge } from "lucide-react";

const Badge = ({ valide }) =>
    valide ? (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">
            ✓ Validé
        </span>
    ) : (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 whitespace-nowrap">
            ⏳ En attente
        </span>
    );

function formatMontant(n) {
    return (n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ══════════════════════════════════════════════════════════
//  PLACEHOLDER — pendant le chargement
// ══════════════════════════════════════════════════════════

const EvenementCardPlaceholder = () => (
    <div className="rounded-2xl overflow-hidden shadow-md bg-white animate-pulse flex flex-col">
        <div className="w-full h-40 bg-gray-200" />
        <div className="p-4 flex flex-col gap-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-6 bg-gray-200 rounded w-full mt-2" />
        </div>
    </div>
);

// ══════════════════════════════════════════════════════════
//  HISTOGRAMME — barres verticales, top événements par ventes
// ══════════════════════════════════════════════════════════

const HistogrammeVentes = ({ donnees }) => {
    if (!donnees || donnees.length === 0) return null;
    const max = Math.max(...donnees.map((d) => d.billets), 1);

    return (
        <div className="flex items-end gap-3 h-40 px-1">
            {donnees.map((d, i) => {
                const hauteur = Math.max(6, (d.billets / max) * 100);
                return (
                    <div key={d.id ?? i} className="flex-1 flex flex-col items-center gap-2 group">
                        <span className="text-[11px] font-bold text-gray-700">{d.billets}</span>
                        <div className="w-full flex items-end h-28">
                            <div
                                className="w-full rounded-t-lg transition-all duration-500 group-hover:opacity-80"
                                style={{
                                    height: `${hauteur}%`,
                                    background: i === 0
                                        ? "linear-gradient(180deg, #7C3AED, #481699)"
                                        : "linear-gradient(180deg, #48169955, #48169930)",
                                }}
                            />
                        </div>
                        <span className="text-[10px] text-gray-400 text-center truncate w-full" title={d.nom}>
                            {d.nom}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// ══════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════

export default function MaBilletterie() {
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 8 });
    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    const [evenementSelectionne, setEvenementSelectionne] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [loadingResa, setLoadingResa] = useState(false);
    // Annulation par l'organisateur : { type: "reservation" | "evenement", cible }
    const [annulation, setAnnulation] = useState(null);
    const [messageAnnulation, setMessageAnnulation] = useState(null);

    const chargerEvenements = useCallback(async (page = 1) => {
        setLoading(true);
        setErreur(null);
        try {
            const data = await getEvenementsBilletterie(page);
            // 🔍 DEBUG — vérifie ici ce que le backend renvoie vraiment
            console.log("[MaBilletterie] Réponse backend :", data);
            setEvents(data.events || []);
            setPagination(data.pagination || { current_page: 1, last_page: 1, total: 0, per_page: 8 });
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const chargerStats = useCallback(async () => {
        setLoadingStats(true);
        try {
            const data = await getStatistiquesBilletterie();
            setStats(data);
        } catch (e) {
            console.error("Erreur stats :", e);
        } finally {
            setLoadingStats(false);
        }
    }, []);

    useEffect(() => {
        chargerEvenements(1);
        chargerStats();
    }, [chargerEvenements, chargerStats]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pagination.last_page) return;
        chargerEvenements(newPage);
    };

    const ouvrirEvenement = async (ev) => {
        setEvenementSelectionne(ev);
        setLoadingResa(true);
        try {
            const toutes = await getReservationsCreateur();
            setReservations(toutes.filter((r) => String(r.evenement_id) === String(ev.id)));
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingResa(false);
        }
    };

    // 🔑 On n'affiche le bloc stats/courbe QUE s'il y a au moins
    // une vente quelque part — sinon ça n'a aucun sens de montrer
    // un graphe à 0 partout.

    // ══════════════════════════════════════════════════════
    //  ÉCRAN 2 — Réservations d'un événement
    // ══════════════════════════════════════════════════════

    const [demandeOuverte, setDemandeOuverte] = useState(false);

    const confirmerAnnulation = async (motif, commentaire) => {
        const res = annulation.type === "reservation"
            ? await annulerReservationOrganisateur(annulation.cible.id, motif, commentaire)
            : await annulerEvenementOrganisateur(annulation.cible.id, motif, commentaire);
        setMessageAnnulation(res.message);
        setAnnulation(null);
        await ouvrirEvenement(evenementSelectionne);
        chargerStats();
    };

    if (evenementSelectionne) {
        const valides = reservations.filter((r) => r.valide).length;
        const actives = reservations.filter((r) => r.statut !== "annulee" && !r.annulee_at);
        return (
            <div className="w-full flex justify-center pb-20 bg-color22 min-h-screen">
                <div className="w-full max-w-6xl px-4 lg:px-6 py-8">
                    <div className="flex items-center gap-3 mb-6">
                        <button
                            onClick={() => setEvenementSelectionne(null)}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-gray-50 transition"
                        >
                            ←
                        </button>
                        <div>
                            <h2 className="text-xl lg:text-2xl font-bold" style={{ color: "#1F2937" }}>
                                {evenementSelectionne.nomEven}
                            </h2>
                            <p className="text-sm text-gray-400">
                                {valides} / {reservations.length} billets validés
                            </p>
                        </div>
                        {actives.length > 0 && (
                            <button
                                onClick={() => setDemandeOuverte(true)}
                                className="ml-auto px-4 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition"
                            >
                                Reporter / annuler l'événement
                            </button>
                        )}
                    </div>

                    {messageAnnulation && (
                        <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm mb-4">
                            {messageAnnulation}
                        </div>
                    )}

                    {demandeOuverte && (
                        <ModaleDemande
                            evenementId={evenementSelectionne.id}
                            nbReservations={actives.length}
                            typeInitial="annulation"
                            onFermer={() => setDemandeOuverte(false)}
                            onEnvoye={(msg) => { setMessageAnnulation(msg); setDemandeOuverte(false); }}
                        />
                    )}

                    {annulation && (
                        <ModaleAnnulation
                            titre={annulation.type === "reservation" ? "Annuler cette réservation ?" : "Annuler tout l'événement ?"}
                            texte={annulation.type === "reservation"
                                ? `${annulation.cible.nom_personne} sera remboursé du montant payé, hors frais de paiement. Cette action est définitive.`
                                : `Les ${actives.length} réservation(s) seront remboursées (montant payé hors frais de paiement) et l'événement sera retiré de l'application. Cette action est définitive.`}
                            motifs={annulation.type === "reservation" ? MOTIFS_RESERVATION : MOTIFS_EVENEMENT}
                            onConfirmer={confirmerAnnulation}
                            onFermer={() => setAnnulation(null)}
                        />
                    )}

                    {loadingResa ? (
                        <div className="text-gray-400 text-center py-20">Chargement…</div>
                    ) : reservations.length === 0 ? (
                        <div className="text-gray-400 text-center py-20 bg-white rounded-2xl border border-gray-200">
                            Aucune réservation pour cet événement.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {reservations.map((r) => (
                                <div key={r.id} className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-xs font-semibold" style={{ color: "#481699" }}>{r.reference}</span>
                                            <Badge valide={r.valide} />
                                        </div>
                                        <div className="text-base font-bold text-gray-800 mt-1 truncate">{r.nom_personne}</div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                                            <span>Places : {r.nombre_personnes}</span>
                                            {r.placement && <span>Placement : {r.placement}</span>}
                                            <span>Réservé le {r.created_at}</span>
                                        </div>
                                    </div>
                                    {r.statut === "annulee" || r.annulee_at ? (
                                        <div className="text-[11px] text-gray-500 bg-gray-100 rounded-lg px-3 py-2 text-center shrink-0">
                                            Annulée le {r.annulee_at}
                                        </div>
                                    ) : !r.valide && (
                                        <div className="flex flex-col items-end gap-2 shrink-0">
                                            <div className="text-[11px] text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2 text-center max-w-[140px]">
                                                À valider via scan QR
                                            </div>
                                            <button
                                                onClick={() => setAnnulation({ type: "reservation", cible: r })}
                                                className="text-[11px] font-semibold text-red-600 hover:underline"
                                            >
                                                Annuler et rembourser
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ══════════════════════════════════════════════════════
    //  ÉCRAN 1 — Stats (si ventes) + Grille paginée
    // ══════════════════════════════════════════════════════

    return (
        <div className="w-full flex justify-center pb-20 bg-white min-h-screen">
            <div className="w-full max-w-6xl px-4 lg:px-6 py-8">

                <div className="flex items-center justify-end mb-6">
                    <button
                        onClick={() => { chargerEvenements(pagination.current_page); chargerStats(); }}
                        className="text-sm font-medium text-color1 hover:opacity-70 transition"
                    >
                        ↻ Actualiser
                    </button>
                </div>

                {/* ══════════ COMPTE DE PAIEMENT STRIPE ══════════ */}
                <CompteStripe retour="/EspaceClient/mabilletterie" />

                {/* ══════════ STATS — masqué si aucune vente ══════════ */}
                {!loadingStats && stats && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <div className="bg-white shadow-sm rounded-2xl p-4 border border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="rounded-full p-2 bg-purple-100" style={{ color: "#481699" }}><Ticket size={16} /></div>
                                    <span className="text-xs text-gray-500 font-medium">Billets vendus</span>
                                </div>
                                <div className="text-2xl font-extrabold text-gray-900">{stats.total_billets}</div>
                            </div>
                            <div className="bg-white shadow-sm rounded-2xl p-4 border border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="rounded-full p-2 bg-green-100 text-green-600"><TrendingUp size={16} /></div>
                                    <span className="text-xs text-gray-500 font-medium">Revenus totaux</span>
                                </div>
                                <div className="text-2xl font-extrabold text-gray-900">{formatMontant(stats.revenu_total)} €</div>
                            </div>
                            <div className="bg-white shadow-sm rounded-2xl p-4 border border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="rounded-full p-2 bg-orange-100 text-orange-600"><Clock size={16} /></div>
                                    <span className="text-xs text-gray-500 font-medium">Non validés</span>
                                </div>
                                <div className="text-2xl font-extrabold text-gray-900">{stats.total_billets_non_valides ?? 0}</div>
                            </div>
                            <div className="bg-white shadow-sm rounded-2xl p-4 border border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="rounded-full p-2 bg-blue-100 text-blue-600"><Gauge size={16} /></div>
                                    <span className="text-xs text-gray-500 font-medium">Remplissage moyen</span>
                                </div>
                                <div className="text-2xl font-extrabold text-gray-900">
                                    {stats.taux_remplissage_moyen != null ? `${stats.taux_remplissage_moyen}%` : "—"}
                                </div>
                            </div>
                        </div>

                        {stats.classement && stats.classement.length > 0 && (
                            <>
                                {/* ══════════ HISTOGRAMME — top ventes ══════════ */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <TrendingUp size={18} style={{ color: "#481699" }} />
                                        <h3 className="font-bold text-gray-800">Ventes par événement</h3>
                                    </div>
                                    <HistogrammeVentes donnees={stats.classement} />
                                </div>

                                {/* ══════════ CLASSEMENT ÉVÉNEMENTS — 3 blocs ══════════ */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Trophy size={18} className="text-amber-500" />
                                        <h3 className="font-bold text-gray-800">Événements les plus vendus</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {stats.classement.map((e, i) => (
                                            <div
                                                key={e.id}
                                                className="rounded-2xl p-4 border flex flex-col gap-2"
                                                style={{
                                                    backgroundColor: i === 0 ? "#FFFBEB" : "#FAFAFA",
                                                    borderColor: i === 0 ? "#FDE68A" : "#E5E7EB",
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div
                                                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                                                        style={{
                                                            backgroundColor: i === 0 ? "#FDE68A" : i === 1 ? "#E5E7EB" : "#FED7AA",
                                                            color: i === 0 ? "#92400E" : i === 1 ? "#374151" : "#9A3412",
                                                        }}
                                                    >
                                                        {i + 1}
                                                    </div>
                                                    {i === 0 && <Trophy size={16} className="text-amber-500" />}
                                                </div>
                                                <span className="text-sm font-bold text-gray-800 truncate">{e.nom}</span>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-lg font-extrabold" style={{ color: "#481699" }}>{e.billets}</span>
                                                    <span className="text-xs text-gray-400">{formatMontant(e.revenu)} €</span>
                                                </div>
                                                <span className="text-[11px] text-gray-400">billets vendus</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══════════ TOP CLIENTS — 3 blocs ══════════ */}
                        {stats.topClients && stats.topClients.length > 0 && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Crown size={18} className="text-amber-500" />
                                    <h3 className="font-bold text-gray-800">Top clients</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {stats.topClients.map((c, i) => (
                                        <div
                                            key={c.id}
                                            className="rounded-2xl p-4 border flex flex-col items-center text-center gap-2"
                                            style={{
                                                backgroundColor: i === 0 ? "#FFFBEB" : "#FAFAFA",
                                                borderColor: i === 0 ? "#FDE68A" : "#E5E7EB",
                                            }}
                                        >
                                            <div
                                                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0"
                                                style={{ backgroundColor: "#48169915", color: "#481699" }}
                                            >
                                                {c.nom?.charAt(0)?.toUpperCase() || "?"}
                                            </div>
                                            <span className="text-sm font-bold text-gray-800 truncate w-full">{c.nom}</span>
                                            <span className="text-[11px] text-gray-400">{c.nbr_reservations} réservation{c.nbr_reservations > 1 ? "s" : ""}</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-lg font-extrabold" style={{ color: "#481699" }}>{c.billets}</span>
                                                <span className="text-xs text-gray-400">billets</span>
                                            </div>
                                            <span className="text-xs text-gray-400">{formatMontant(c.revenu)} €</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {erreur && (
                    <div className="bg-red-50 text-red-600 rounded-xl p-4 mb-4 border border-red-200">{erreur}</div>
                )}

                {/* ══════════ GRILLE — squelette pendant le chargement ══════════ */}
                {loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {Array.from({ length: pagination?.per_page || 8 }).map((_, i) => (
                            <EvenementCardPlaceholder key={i} />
                        ))}
                    </div>
                )}

                {/* ══════════ GRILLE — événements ══════════ */}
                {!loading && events.length === 0 && (
                    <div className="text-gray-400 text-center py-20 bg-white rounded-2xl border border-gray-200">
                        Aucun événement avec billetterie Thrundrz pour le moment.
                    </div>
                )}

                {!loading && events.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {events.map((ev) => {
                            const tauxRemplissage = ev.nbr_places_max
                                ? Math.min(100, Math.round((ev.nbr_billets_vendus / ev.nbr_places_max) * 100))
                                : null;

                            return (
                                <div
                                    key={ev.id}
                                    onClick={() => ouvrirEvenement(ev)}
                                    className="group rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer"
                                >
                                    {/* ── Image / visuel ── */}
                                    <div className="relative h-44 overflow-hidden">
                                        {ev.imgEven && ev.imgEven !== "empty" ? (
                                            <img
                                                src={ev.imgEven.split("#")[0]}
                                                alt={ev.nomEven}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div
                                                className="w-full h-full flex flex-col items-center justify-center gap-2"
                                                style={{ background: "linear-gradient(135deg, #48169912, #4816990A)" }}
                                            >
                                                <ImageIcon size={28} style={{ color: "#48169940" }} />
                                                <span className="text-[11px] font-medium" style={{ color: "#48169960" }}>
                                                    Aucune image
                                                </span>
                                            </div>
                                        )}

                                        {/* Voile dégradé pour la lisibilité de la date */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                                        {ev.dateEven && (
                                            <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/95 backdrop-blur px-2.5 py-1 rounded-lg text-[11px] font-semibold text-gray-800 shadow-sm">
                                                <Calendar size={12} style={{ color: "#481699" }} />
                                                {ev.dateEven}
                                            </div>
                                        )}
                                    </div>

                                    {/* ── Infos ── */}
                                    <div className="p-4 flex flex-col gap-3 flex-1">
                                        <h3 className="font-bold text-gray-900 text-[15px] leading-tight truncate">
                                            {ev.nomEven}
                                        </h3>

                                        {/* Statistiques rapides — icône + valeur */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <Users size={13} className="text-gray-400" />
                                                <span><span className="font-semibold text-gray-700">{ev.nbr_demandes}</span> demande{ev.nbr_demandes > 1 ? "s" : ""}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <CheckCircle2 size={13} className="text-green-500" />
                                                <span><span className="font-semibold text-gray-700">{ev.nbr_valides}</span> validée{ev.nbr_valides > 1 ? "s" : ""}</span>
                                            </div>
                                        </div>

                                        {/* Barre de remplissage — style dashboard */}
                                        <div className="mt-1">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-[11px] font-medium text-gray-400">
                                                    {ev.nbr_billets_vendus} billet{ev.nbr_billets_vendus > 1 ? "s" : ""} vendu{ev.nbr_billets_vendus > 1 ? "s" : ""}
                                                </span>
                                                {ev.nbr_places_max != null && (
                                                    <span className="text-[11px] font-bold" style={{ color: "#481699" }}>
                                                        {tauxRemplissage}%
                                                    </span>
                                                )}
                                            </div>
                                            {ev.nbr_places_max != null ? (
                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full transition-all duration-500"
                                                        style={{
                                                            width: `${tauxRemplissage}%`,
                                                            background: "linear-gradient(90deg, #7C3AED, #481699)",
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-full h-1.5 bg-gray-50 rounded-full" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ══════════ PAGINATION — identique à MesEvents.jsx ══════════ */}
                {pagination.last_page > 1 && (
                    <div className="flex w-full justify-center items-center gap-3 mt-8">
                        <button
                            disabled={pagination.current_page === 1}
                            onClick={() => handlePageChange(pagination.current_page - 1)}
                            className={`px-4 py-1 rounded-lg text-white text-sm font-semibold ${pagination.current_page === 1 ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                        >
                            Précédent
                        </button>

                        <span className="text-color11 font-medium text-sm">
                            {pagination.current_page} / {pagination.last_page}
                        </span>

                        <button
                            disabled={pagination.current_page === pagination.last_page}
                            onClick={() => handlePageChange(pagination.current_page + 1)}
                            className={`px-4 py-1 rounded-lg text-white text-sm font-semibold ${pagination.current_page === pagination.last_page ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}